// Ficha pública /eventos/:slug — detallada con CTA Inscribirse (login-gate).

import { useEffect, useState } from 'react';
import { useParams, useNavigate, Link, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  ArrowLeft, Calendar, MapPin, Clock, Users, Sparkles, AlertCircle,
  Loader2, CheckCircle2, Instagram, Globe,
} from 'lucide-react';
import CodigoDescuento from '../components/CodigoDescuento';
import { getEvento, inscribirEvento, ApiError } from '../lib/api';
import { useAuth, readToken } from '../context/AuthContext';

function formatEuros(n) {
  if (n == null) return '';
  const num = Number(n);
  if (Number.isInteger(num)) return `${num} €`;
  return `${num.toFixed(2).replace('.', ',')} €`;
}
function formatFechaLarga(iso, lang) {
  if (!iso) return '';
  const d = new Date(iso + 'T00:00:00');
  const map = { es: 'es-ES', en: 'en-GB', ru: 'ru-RU', fr: 'fr-FR' };
  return new Intl.DateTimeFormat(map[lang] || 'es-ES', {
    weekday: 'long', day: 'numeric', month: 'long', year: 'numeric',
  }).format(d);
}

export default function Evento() {
  const { slug } = useParams();
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const { isAuthenticated } = useAuth();
  const lang = (i18n.resolvedLanguage || 'es').toLowerCase();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [inscribiendo, setInscribiendo] = useState(false);
  const [errorInsc, setErrorInsc] = useState(null);
  const [codigoSel, setCodigoSel] = useState(null);
  const [bannerExito, setBannerExito] = useState(null);

  useEffect(() => {
    let cancelled = false;
    setData(null); setError(null);
    getEvento(slug, lang)
      .then((j) => { if (!cancelled) setData(j); })
      .catch((e) => { if (!cancelled) setError(e); });
    return () => { cancelled = true; };
  }, [slug, lang]);

  // Banner success/cancelled tras Stripe redirect
  useEffect(() => {
    const s = params.get('stripe');
    if (s === 'success') setBannerExito('success');
    if (s === 'cancelled') setBannerExito('cancelled');
  }, [params]);

  if (error) {
    return (
      <main className="clase-page">
        <div className="clase-page-inner">
          <button type="button" className="clase-back" onClick={() => navigate(-1)}>
            <ArrowLeft size={16} strokeWidth={1.5} /> {t('comun.volver')}
          </button>
          <div className="clase-page-error">
            {error?.code === 'EVENTO_NO_ENCONTRADO'
              ? t('eventos.no_encontrado')
              : t('eventos.error_carga')}
          </div>
        </div>
      </main>
    );
  }

  if (!data) {
    return (
      <main className="clase-page">
        <div className="clase-page-inner">
          <div className="clase-page-skeleton" />
        </div>
      </main>
    );
  }

  const { evento } = data;
  const tieneDescLarga = !!(evento.descripcion_larga && evento.descripcion_larga.trim());
  const lleno = evento.plazas_restantes === 0;
  const hoy = new Date().toISOString().slice(0, 10);
  const pasado = evento.fecha < hoy;

  const onInscribir = async () => {
    if (!evento?.id) return;
    setErrorInsc(null);
    if (!isAuthenticated) {
      navigate(`/login?next=${encodeURIComponent(`/eventos/${slug}`)}`);
      return;
    }
    const token = readToken();
    if (!token) {
      navigate(`/login?next=${encodeURIComponent(`/eventos/${slug}`)}`);
      return;
    }
    setInscribiendo(true);
    try {
      const r = await inscribirEvento(slug, codigoSel?.codigo ? { codigo_descuento: codigoSel.codigo } : {}, token);
      if (r?.gratuito) {
        // Sin pago, inscripción confirmada directa.
        setBannerExito('gratuito');
      } else if (r?.url) {
        window.location.href = r.url;
      } else {
        setErrorInsc(t('eventos.error_inscripcion'));
        setInscribiendo(false);
      }
    } catch (e) {
      const code = e instanceof ApiError ? e.code : null;
      setErrorInsc(t('eventos.errores.' + code, { defaultValue: t('eventos.error_inscripcion') }));
      setInscribiendo(false);
    }
  };

  return (
    <main className="clase-page evento-page">
      <div className="clase-page-inner">
        <button type="button" className="clase-back" onClick={() => navigate(-1)}>
          <ArrowLeft size={16} strokeWidth={1.5} /> {t('comun.volver')}
        </button>

        {bannerExito === 'success' && (
          <div className="evento-banner evento-banner--ok">
            <CheckCircle2 size={18} strokeWidth={1.75} />
            <span>{t('eventos.banner_pago_ok')}</span>
          </div>
        )}
        {bannerExito === 'cancelled' && (
          <div className="evento-banner evento-banner--warn">
            <AlertCircle size={18} strokeWidth={1.75} />
            <span>{t('eventos.banner_pago_cancelado')}</span>
          </div>
        )}
        {bannerExito === 'gratuito' && (
          <div className="evento-banner evento-banner--ok">
            <CheckCircle2 size={18} strokeWidth={1.75} />
            <span>{t('eventos.banner_inscripcion_ok')}</span>
          </div>
        )}

        <header className="evento-page-head">
          {evento.imagen_url ? (
            <div className="evento-page-foto">
              <img src={evento.imagen_url} alt={evento.nombre} />
            </div>
          ) : (
            <div className="evento-page-foto evento-page-foto--placeholder">
              <Sparkles size={48} strokeWidth={1.2} />
            </div>
          )}
          <div className="evento-page-head-info">
            <h1 className="clase-page-title">{evento.nombre}</h1>
            <div className="evento-page-meta">
              <span><Calendar size={14} strokeWidth={1.5} /> {formatFechaLarga(evento.fecha, lang)}</span>
              <span><Clock size={14} strokeWidth={1.5} /> {evento.hora_inicio} · {evento.duracion_minutos} {t('eventos.min')}</span>
              <span><MapPin size={14} strokeWidth={1.5} /> {evento.lugar}</span>
              {!pasado && <span><Users size={14} strokeWidth={1.5} /> {evento.plazas_restantes} / {evento.aforo_maximo} {t('eventos.plazas')}</span>}
            </div>
            <div className="evento-page-precio">
              {evento.precio_euros > 0 ? formatEuros(evento.precio_euros) : t('eventos.gratis')}
            </div>
          </div>
        </header>

        <p className="clase-page-resumen">{evento.descripcion}</p>

        <section className="clase-page-cuerpo">
          {tieneDescLarga ? (
            evento.descripcion_larga.split(/\n\n+/).map((parrafo, i) => (
              <p key={i} className="clase-page-parrafo">{parrafo.trim()}</p>
            ))
          ) : (
            <p className="clase-page-placeholder">{t('eventos.placeholder_sin_contenido')}</p>
          )}
        </section>

        {evento.profesor && (
          <section className="evento-profesor">
            <h2 className="clase-page-sec-title">{t('eventos.facilita')}</h2>
            <div className="evento-profesor-card">
              {evento.profesor.foto_url
                ? <img src={evento.profesor.foto_url} alt={evento.profesor.nombre_publico} />
                : <div className="evento-profesor-iniciales">
                    {evento.profesor.nombre_publico.split(/\s+/).map((p) => p[0]).slice(0, 2).join('').toUpperCase()}
                  </div>}
              <div>
                <div className="evento-profesor-nombre">{evento.profesor.nombre_publico}</div>
                {evento.profesor.bio_corta && <p className="evento-profesor-bio">{evento.profesor.bio_corta}</p>}
              </div>
            </div>
          </section>
        )}

        {Array.isArray(evento.participantes) && evento.participantes.length > 0 && (
          <section className="evento-participantes">
            <h2 className="clase-page-sec-title">{t('eventos.participantes_titulo')}</h2>
            <div className="evento-participantes-grid">
              {evento.participantes.map((p) => (
                <article key={p.id} className="evento-participante-card">
                  {p.foto_url
                    ? <img src={p.foto_url} alt={p.nombre || ''} className="evento-participante-foto" />
                    : <div className="evento-participante-iniciales">
                        {(p.nombre || '?').split(/\s+/).map((s) => s[0]).slice(0, 2).join('').toUpperCase()}
                      </div>}
                  <div className="evento-participante-info">
                    <div className="evento-participante-nombre">
                      {p.nombre || '—'}
                      {p.tipo === 'invitado' && (
                        <span className="evento-participante-pill">{t('eventos.invitado')}</span>
                      )}
                    </div>
                    {p.rol_texto && <div className="evento-participante-rol">{p.rol_texto}</div>}
                    {p.bio && <p className="evento-participante-bio">{p.bio}</p>}
                    {(p.instagram || p.web_url) && (
                      <div className="evento-participante-redes">
                        {p.instagram && (
                          <a href={`https://instagram.com/${String(p.instagram).replace(/^@/, '')}`}
                             target="_blank" rel="noopener noreferrer" aria-label="Instagram">
                            <Instagram size={14} strokeWidth={1.5} /> @{String(p.instagram).replace(/^@/, '')}
                          </a>
                        )}
                        {p.web_url && (
                          <a href={p.web_url} target="_blank" rel="noopener noreferrer" aria-label="Web">
                            <Globe size={14} strokeWidth={1.5} /> {t('eventos.web')}
                          </a>
                        )}
                      </div>
                    )}
                  </div>
                </article>
              ))}
            </div>
          </section>
        )}

        {errorInsc && (
          <div className="modal-error-general bono-page-error">
            <AlertCircle size={16} strokeWidth={1.75} />
            <span>{errorInsc}</span>
          </div>
        )}

        {!pasado && !lleno && evento.precio_euros > 0 && isAuthenticated && (
          <CodigoDescuento
            ambito="eventos"
            params={{ evento_slug: slug }}
            token={readToken()}
            onChange={setCodigoSel}
            disabled={inscribiendo}
          />
        )}

        {!pasado && (
          <div className="evento-page-cta-row">
            <button
              type="button"
              className="clase-page-cta evento-page-cta-inscribir"
              onClick={onInscribir}
              disabled={inscribiendo || lleno}
            >
              {inscribiendo ? (
                <><Loader2 size={16} strokeWidth={1.5} className="spin" /> {t('auth.enviando')}</>
              ) : lleno ? (
                t('eventos.lleno')
              ) : evento.precio_euros > 0 ? (
                t('eventos.cta_inscribir_pagado', { precio: formatEuros(evento.precio_euros) })
              ) : (
                t('eventos.cta_inscribir_gratis')
              )}
            </button>
            <Link to="/eventos" className="clase-page-cta clase-page-cta--secondary">
              {t('eventos.ver_otros')}
            </Link>
          </div>
        )}

        {!pasado && evento.cancelacion_dias_antes > 0 && evento.precio_euros > 0 && (
          <p className="evento-page-aviso">{t('eventos.aviso_cancelacion', { dias: evento.cancelacion_dias_antes })}</p>
        )}
      </div>
    </main>
  );
}
